import type * as Y from 'yjs';

export type LegacyArchiveXYWH = [number, number, number, number];

export type LegacyArchiveSerializedXYWH =
  `[${number},${number},${number},${number}]`;

export type LegacyYDocUpgradeDiagnosticReason =
  | 'missing'
  | 'invalid'
  | 'nested-non-canvas'
  | 'zero-size';

export type LegacyYDocUpgradeDiagnosticScope = 'block' | 'surface-element';
export type LegacyYDocUpgradeDiagnosticAction = 'drop' | 'repair';

export type LegacyYDocUpgradeDiagnostic = {
  readonly action: LegacyYDocUpgradeDiagnosticAction;
  readonly blockId?: string;
  readonly docId?: string;
  readonly elementId?: string;
  readonly elementType?: string;
  readonly flavour?: string;
  readonly parentFlavour?: string;
  readonly parentId?: string;
  readonly persistedValue: unknown;
  readonly reason: LegacyYDocUpgradeDiagnosticReason;
  readonly repairedValue?: LegacyArchiveSerializedXYWH;
  readonly scope: LegacyYDocUpgradeDiagnosticScope;
  readonly source?: string;
  readonly sourceDocId?: string;
  readonly upgrade: 'canvas-geometry-v1';
};

export type UpgradeLegacyYDocArchiveInput = {
  readonly docId?: string;
  readonly onDiagnostic?: (diagnostic: LegacyYDocUpgradeDiagnostic) => void;
  readonly source?: string;
  readonly sourceDocId?: string;
  readonly systemDoc?: boolean;
  readonly ydoc: Y.Doc;
};

export type UpgradeLegacyYDocArchiveResult = {
  readonly changed: boolean;
  readonly diagnostics: readonly LegacyYDocUpgradeDiagnostic[];
};

export type LegacyDocSnapshotLike = {
  readonly blocks?: LegacyBlockSnapshotLike;
  readonly meta?: { readonly id?: unknown };
};

export type UpgradeLegacyDocSnapshotInput = {
  readonly docId?: string;
  readonly onDiagnostic?: (diagnostic: LegacyYDocUpgradeDiagnostic) => void;
  readonly snapshot: LegacyDocSnapshotLike;
  readonly source?: string;
  readonly sourceDocId?: string;
  readonly systemDoc?: boolean;
};

type LegacyBlockSnapshotLike = {
  readonly children?: LegacyBlockSnapshotLike[];
  readonly flavour?: unknown;
  readonly id?: unknown;
  readonly props?: Record<string, unknown>;
};

type MutableXYWH = [number, number, number, number];

type BlockEntry = {
  readonly flavour?: string;
  readonly id: string;
  readonly map: Y.Map<unknown>;
  readonly parentFlavour?: string;
  readonly parentId?: string;
};

type SurfaceElementEntry = {
  readonly id: string;
  readonly map: Y.Map<unknown>;
  readonly type?: string;
};

const CANVAS_BLOCK_FALLBACK_SIZE_BY_FLAVOUR = new Map<string, [number, number]>(
  [
    ['affine:note', [498, 92]],
    ['affine:frame', [800, 600]],
    ['affine:image', [100, 100]],
    ['affine:latex', [16, 16]],
    ['affine:database', [800, 300]],
    ['affine:attachment', [240, 84]],
    ['affine:bookmark', [240, 84]],
    ['affine:embed', [400, 300]],
    ['affine:embed-github', [400, 300]],
    ['affine:embed-html', [400, 300]],
    ['affine:embed-iframe', [400, 300]],
    ['affine:embed-loom', [400, 300]],
    ['affine:embed-synced-doc', [400, 300]],
    ['affine:embed-youtube', [400, 300]],
    ['affine:edgeless-text', [160, 40]],
    ['affine:surface-ref', [400, 300]],
    ['affine:automation', [480, 240]],
  ]
);

const SURFACE_ELEMENT_FALLBACK_SIZE_BY_TYPE = new Map<string, [number, number]>(
  [
    ['connector', [120, 16]],
    ['group', [100, 100]],
    ['mindmap', [240, 120]],
    ['shape', [100, 100]],
    ['text', [160, 40]],
  ]
);

const MIN_CONNECTOR_SIZE = 16;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isXYWH(value: unknown): value is LegacyArchiveXYWH {
  return (
    Array.isArray(value) && value.length === 4 && value.every(isFiniteNumber)
  );
}

export function parseLegacyArchiveXYWH(
  value: unknown
): LegacyArchiveXYWH | null {
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value) as unknown;
    return isXYWH(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function hasPositiveSize(xywh: LegacyArchiveXYWH): boolean {
  return xywh[2] > 0 && xywh[3] > 0;
}

export function serializeLegacyArchiveXYWH(
  xywh: LegacyArchiveXYWH
): LegacyArchiveSerializedXYWH {
  if (!isXYWH(xywh)) {
    throw new TypeError('xywh must be a finite [x,y,width,height] tuple');
  }
  if (!hasPositiveSize(xywh)) {
    throw new TypeError('xywh must have positive width and height');
  }

  return JSON.stringify(xywh) as LegacyArchiveSerializedXYWH;
}

function isYMap(value: unknown): value is Y.Map<unknown> {
  return (
    isRecord(value) &&
    typeof (value as { forEach?: unknown }).forEach === 'function' &&
    typeof (value as { get?: unknown }).get === 'function' &&
    typeof (value as { has?: unknown }).has === 'function' &&
    typeof (value as { set?: unknown }).set === 'function'
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readString(map: Y.Map<unknown>, key: string): string | undefined {
  const value = map.get(key);
  return typeof value === 'string' ? value : undefined;
}

function readNumber(map: Y.Map<unknown>, key: string): number | undefined {
  const value = map.get(key);
  return isFiniteNumber(value) ? value : undefined;
}

function readObjectProperty(value: unknown, key: string): unknown {
  if (isYMap(value)) return value.get(key);
  if (isRecord(value)) return value[key];
  return undefined;
}

function readPoint(value: unknown): [number, number] | null {
  if (
    Array.isArray(value) &&
    value.length === 2 &&
    isFiniteNumber(value[0]) &&
    isFiniteNumber(value[1])
  ) {
    return [value[0], value[1]];
  }

  if (typeof value === 'string') {
    try {
      return readPoint(JSON.parse(value) as unknown);
    } catch {
      return null;
    }
  }

  if (isRecord(value)) {
    const x = value.x;
    const y = value.y;
    if (isFiniteNumber(x) && isFiniteNumber(y)) {
      return [x, y];
    }
  }

  return null;
}

function normalizeXYWH(
  value: unknown,
  fallback: LegacyArchiveXYWH
): {
  readonly reason: LegacyYDocUpgradeDiagnosticReason;
  readonly xywh: LegacyArchiveXYWH;
} | null {
  const parsed = parseLegacyArchiveXYWH(value);
  if (parsed && hasPositiveSize(parsed)) {
    return null;
  }

  if (parsed) {
    return {
      reason: 'zero-size',
      xywh: [
        parsed[0],
        parsed[1],
        parsed[2] > 0 ? parsed[2] : fallback[2],
        parsed[3] > 0 ? parsed[3] : fallback[3],
      ],
    };
  }

  return {
    reason: value === undefined || value === null ? 'missing' : 'invalid',
    xywh: fallback,
  };
}

function fallbackXYWH(
  value: unknown,
  size: readonly [number, number]
): LegacyArchiveXYWH {
  const parsed = parseLegacyArchiveXYWH(value);
  return [parsed?.[0] ?? 0, parsed?.[1] ?? 0, size[0], size[1]];
}

function unionBounds(
  current: MutableXYWH | null,
  next: LegacyArchiveXYWH
): MutableXYWH {
  if (!current) {
    return [next[0], next[1], next[2], next[3]];
  }

  const minX = Math.min(current[0], next[0]);
  const minY = Math.min(current[1], next[1]);
  const maxX = Math.max(current[0] + current[2], next[0] + next[2]);
  const maxY = Math.max(current[1] + current[3], next[1] + next[3]);
  return [minX, minY, maxX - minX, maxY - minY];
}

function padBound(
  bound: LegacyArchiveXYWH,
  padding: number
): LegacyArchiveXYWH {
  return [
    bound[0] - padding,
    bound[1] - padding,
    Math.max(MIN_CONNECTOR_SIZE, bound[2] + padding * 2),
    Math.max(MIN_CONNECTOR_SIZE, bound[3] + padding * 2),
  ];
}

function collectChildIds(value: unknown): string[] {
  const ids: string[] = [];
  if (isYMap(value)) {
    value.forEach((childValue, key) => {
      if (childValue !== false) ids.push(key);
    });
    return ids;
  }

  if (isRecord(value)) {
    for (const [key, childValue] of Object.entries(value)) {
      if (childValue !== false) ids.push(key);
    }
  }

  return ids;
}

function collectBlockChildIds(value: unknown): string[] {
  if (
    value &&
    typeof value === 'object' &&
    typeof (value as { toArray?: unknown }).toArray === 'function'
  ) {
    return (value as { toArray: () => unknown[] })
      .toArray()
      .filter((id): id is string => typeof id === 'string');
  }

  if (Array.isArray(value)) {
    return value.filter((id): id is string => typeof id === 'string');
  }

  return collectChildIds(value);
}

function collectSnapshotBlocks(
  block: LegacyBlockSnapshotLike | undefined,
  callback: (
    block: LegacyBlockSnapshotLike,
    parent: LegacyBlockSnapshotLike | undefined
  ) => void,
  parent?: LegacyBlockSnapshotLike
) {
  if (!block) return;
  callback(block, parent);
  for (const child of block.children ?? []) {
    collectSnapshotBlocks(child, callback, block);
  }
}

function isCanvasRenderedBlockParent(parentFlavour: string | undefined) {
  return parentFlavour === 'affine:page' || parentFlavour === 'affine:surface';
}

function isLikelyDefaultNestedXYWH(
  flavour: string | undefined,
  xywh: LegacyArchiveXYWH | null
) {
  if (!flavour || !xywh) return false;
  const size = CANVAS_BLOCK_FALLBACK_SIZE_BY_FLAVOUR.get(flavour);
  return (
    !!size &&
    xywh[0] === 0 &&
    xywh[1] === 0 &&
    xywh[2] === size[0] &&
    xywh[3] === size[1]
  );
}

function shouldDropNestedNonCanvasXYWH(input: {
  readonly flavour?: string;
  readonly parentFlavour?: string;
  readonly xywhValue: unknown;
}) {
  if (isCanvasRenderedBlockParent(input.parentFlavour)) return false;
  const parsed = parseLegacyArchiveXYWH(input.xywhValue);
  return (
    !parsed ||
    !hasPositiveSize(parsed) ||
    isLikelyDefaultNestedXYWH(input.flavour, parsed)
  );
}

function readConnectionPoint(
  connection: unknown,
  resolveBound: (id: string) => LegacyArchiveXYWH | null
): [number, number] | null {
  const id = readObjectProperty(connection, 'id');
  const position = readPoint(readObjectProperty(connection, 'position'));

  if (typeof id === 'string') {
    const bound = resolveBound(id);
    if (!bound) return null;
    const [relativeX, relativeY] = position ?? [0.5, 0.5];
    return [bound[0] + bound[2] * relativeX, bound[1] + bound[3] * relativeY];
  }

  return position;
}

function upgradeLegacySnapshotGeometry(
  input: UpgradeLegacyDocSnapshotInput
): UpgradeLegacyYDocArchiveResult {
  const diagnostics: LegacyYDocUpgradeDiagnostic[] = [];
  const docId =
    input.docId ??
    (typeof input.snapshot.meta?.id === 'string'
      ? input.snapshot.meta.id
      : undefined);
  const blockBounds = new Map<string, LegacyArchiveXYWH>();
  const surfaceElements = new Map<string, Record<string, unknown>>();
  let changed = false;

  const recordDiagnostic = (
    diagnostic: Omit<
      LegacyYDocUpgradeDiagnostic,
      'docId' | 'source' | 'sourceDocId' | 'upgrade'
    >
  ) => {
    const fullDiagnostic: LegacyYDocUpgradeDiagnostic = {
      ...diagnostic,
      docId,
      source: input.source,
      sourceDocId: input.sourceDocId,
      upgrade: 'canvas-geometry-v1',
    };
    diagnostics.push(fullDiagnostic);
    input.onDiagnostic?.(fullDiagnostic);
  };

  collectSnapshotBlocks(input.snapshot.blocks, (block, parent) => {
    if (typeof block.id !== 'string' || typeof block.flavour !== 'string') {
      return;
    }
    const props = block.props;
    if (!props) return;

    const xywhValue = props.xywh;
    const size = CANVAS_BLOCK_FALLBACK_SIZE_BY_FLAVOUR.get(block.flavour);
    const parentFlavour =
      typeof parent?.flavour === 'string' ? parent.flavour : undefined;
    const parentId = typeof parent?.id === 'string' ? parent.id : undefined;
    const isCanvasRendered = isCanvasRenderedBlockParent(parentFlavour);

    if (
      Object.prototype.hasOwnProperty.call(props, 'xywh') &&
      !isCanvasRendered &&
      shouldDropNestedNonCanvasXYWH({
        flavour: block.flavour,
        parentFlavour,
        xywhValue,
      })
    ) {
      delete props.xywh;
      changed = true;
      recordDiagnostic({
        action: 'drop',
        blockId: block.id,
        flavour: block.flavour,
        parentFlavour,
        parentId,
        persistedValue: xywhValue,
        reason: 'nested-non-canvas',
        scope: 'block',
      });
      return;
    }

    const shouldRepair =
      isCanvasRendered &&
      (Object.prototype.hasOwnProperty.call(props, 'xywh') ||
        (!input.systemDoc && size !== undefined));

    if (shouldRepair && size) {
      const normalized = normalizeXYWH(
        xywhValue,
        fallbackXYWH(xywhValue, size)
      );
      if (normalized) {
        const repairedValue = serializeLegacyArchiveXYWH(normalized.xywh);
        props.xywh = repairedValue;
        changed = true;
        recordDiagnostic({
          action: 'repair',
          blockId: block.id,
          flavour: block.flavour,
          parentFlavour,
          parentId,
          persistedValue: xywhValue,
          reason: normalized.reason,
          repairedValue,
          scope: 'block',
        });
        blockBounds.set(block.id, normalized.xywh);
      } else {
        const parsed = parseLegacyArchiveXYWH(xywhValue);
        if (parsed) blockBounds.set(block.id, parsed);
      }
    }

    if (block.flavour !== 'affine:surface') return;
    const boxedElements = props.elements;
    const elements =
      isRecord(boxedElements) && isRecord(boxedElements.value)
        ? boxedElements.value
        : null;
    if (!elements) return;

    for (const [elementId, element] of Object.entries(elements)) {
      if (isRecord(element)) {
        surfaceElements.set(elementId, element);
      }
    }
  });

  const elementBounds = new Map<string, LegacyArchiveXYWH>();
  const resolving = new Set<string>();
  const resolveAnyBound = (id: string): LegacyArchiveXYWH | null =>
    blockBounds.get(id) ?? resolveElementBound(id);

  const computeConnectorBound = (
    element: Record<string, unknown>
  ): LegacyArchiveXYWH | null => {
    const sourcePoint = readConnectionPoint(element.source, resolveAnyBound);
    const targetPoint = readConnectionPoint(element.target, resolveAnyBound);
    let bound: MutableXYWH | null = null;

    for (const point of [sourcePoint, targetPoint]) {
      if (!point) continue;
      bound = unionBounds(bound, [point[0], point[1], 0, 0]);
    }

    if (Array.isArray(element.labelXYWH) && isXYWH(element.labelXYWH)) {
      bound = unionBounds(bound, element.labelXYWH);
    }

    if (!bound) return null;
    const strokeWidth = isFiniteNumber(element.strokeWidth)
      ? element.strokeWidth
      : 1;
    return padBound(bound, Math.max(8, strokeWidth * 2));
  };

  const computeChildrenBound = (
    element: Record<string, unknown>
  ): LegacyArchiveXYWH | null => {
    let bound: MutableXYWH | null = null;
    for (const childId of collectChildIds(element.children)) {
      const childBound = resolveAnyBound(childId);
      if (!childBound) continue;
      bound = unionBounds(bound, childBound);
    }
    return bound;
  };

  function resolveElementBound(id: string): LegacyArchiveXYWH | null {
    const existing = elementBounds.get(id);
    if (existing) return existing;

    const element = surfaceElements.get(id);
    if (!element || resolving.has(id)) return null;

    resolving.add(id);
    try {
      const type = typeof element.type === 'string' ? element.type : undefined;
      const xywhValue = element.xywh;
      const parsed = parseLegacyArchiveXYWH(xywhValue);
      if (parsed && hasPositiveSize(parsed)) {
        elementBounds.set(id, parsed);
        return parsed;
      }

      const size = type
        ? SURFACE_ELEMENT_FALLBACK_SIZE_BY_TYPE.get(type)
        : undefined;
      const computed =
        type === 'connector'
          ? computeConnectorBound(element)
          : type === 'group' || type === 'mindmap'
            ? computeChildrenBound(element)
            : null;
      const fallback = computed ?? fallbackXYWH(xywhValue, size ?? [100, 100]);
      const normalized = normalizeXYWH(xywhValue, fallback);
      if (!normalized) return null;

      const repairedValue = serializeLegacyArchiveXYWH(normalized.xywh);
      element.xywh = repairedValue;
      changed = true;
      elementBounds.set(id, normalized.xywh);
      recordDiagnostic({
        action: 'repair',
        elementId: id,
        elementType: type,
        persistedValue: xywhValue,
        reason: normalized.reason,
        repairedValue,
        scope: 'surface-element',
      });
      return normalized.xywh;
    } finally {
      resolving.delete(id);
    }
  }

  for (const elementId of surfaceElements.keys()) {
    resolveElementBound(elementId);
  }

  return { changed, diagnostics };
}

export function upgradeLegacyDocSnapshot(
  input: UpgradeLegacyDocSnapshotInput
): UpgradeLegacyYDocArchiveResult {
  return upgradeLegacySnapshotGeometry(input);
}

export function upgradeLegacyYDocArchive(
  input: UpgradeLegacyYDocArchiveInput
): UpgradeLegacyYDocArchiveResult {
  const diagnostics: LegacyYDocUpgradeDiagnostic[] = [];
  const blocks = input.ydoc.getMap('blocks');
  const blockBounds = new Map<string, LegacyArchiveXYWH>();
  const blockEntries = new Map<string, BlockEntry>();
  const blockParentIds = new Map<string, string>();
  const blockFlavours = new Map<string, string | undefined>();
  const surfaceElements = new Map<string, SurfaceElementEntry>();
  let changed = false;

  const recordDiagnostic = (
    diagnostic: Omit<
      LegacyYDocUpgradeDiagnostic,
      'docId' | 'source' | 'sourceDocId' | 'upgrade'
    >
  ) => {
    const fullDiagnostic: LegacyYDocUpgradeDiagnostic = {
      ...diagnostic,
      docId: input.docId,
      source: input.source,
      sourceDocId: input.sourceDocId,
      upgrade: 'canvas-geometry-v1',
    };
    diagnostics.push(fullDiagnostic);
    input.onDiagnostic?.(fullDiagnostic);
  };

  blocks.forEach((value, id) => {
    if (!isYMap(value)) return;

    const flavour = readString(value, 'sys:flavour');
    blockEntries.set(id, { flavour, id, map: value });
    blockFlavours.set(id, flavour);
  });

  blockEntries.forEach(entry => {
    for (const childId of collectBlockChildIds(entry.map.get('sys:children'))) {
      blockParentIds.set(childId, entry.id);
    }
  });

  const blockEntryWithParent = (entry: BlockEntry): BlockEntry => {
    const parentId = blockParentIds.get(entry.id);
    return {
      ...entry,
      parentFlavour: parentId ? blockFlavours.get(parentId) : undefined,
      parentId,
    };
  };

  const repairBlock = (entry: BlockEntry) => {
    const xywhValue = entry.map.get('prop:xywh');
    const size = entry.flavour
      ? CANVAS_BLOCK_FALLBACK_SIZE_BY_FLAVOUR.get(entry.flavour)
      : undefined;
    const isCanvasRendered = isCanvasRenderedBlockParent(entry.parentFlavour);

    if (
      entry.map.has('prop:xywh') &&
      !isCanvasRendered &&
      shouldDropNestedNonCanvasXYWH({
        flavour: entry.flavour,
        parentFlavour: entry.parentFlavour,
        xywhValue,
      })
    ) {
      entry.map.delete('prop:xywh');
      changed = true;
      recordDiagnostic({
        action: 'drop',
        blockId: entry.id,
        flavour: entry.flavour,
        parentFlavour: entry.parentFlavour,
        parentId: entry.parentId,
        persistedValue: xywhValue,
        reason: 'nested-non-canvas',
        scope: 'block',
      });
      return;
    }

    const shouldRepair =
      isCanvasRendered &&
      (entry.map.has('prop:xywh') || (!input.systemDoc && size !== undefined));

    if (!shouldRepair || !size) {
      const parsed = isCanvasRendered
        ? parseLegacyArchiveXYWH(xywhValue)
        : null;
      if (parsed && hasPositiveSize(parsed)) {
        blockBounds.set(entry.id, parsed);
      }
      return;
    }

    const normalized = normalizeXYWH(xywhValue, fallbackXYWH(xywhValue, size));
    if (normalized) {
      const repairedValue = serializeLegacyArchiveXYWH(normalized.xywh);
      entry.map.set('prop:xywh', repairedValue);
      changed = true;
      recordDiagnostic({
        action: 'repair',
        blockId: entry.id,
        flavour: entry.flavour,
        parentFlavour: entry.parentFlavour,
        parentId: entry.parentId,
        persistedValue: xywhValue,
        reason: normalized.reason,
        repairedValue,
        scope: 'block',
      });
      blockBounds.set(entry.id, normalized.xywh);
      return;
    }

    const parsed = parseLegacyArchiveXYWH(xywhValue);
    if (parsed) blockBounds.set(entry.id, parsed);
  };

  blockEntries.forEach(entry => {
    const entryWithParent = blockEntryWithParent(entry);
    repairBlock(entryWithParent);

    if (entry.flavour !== 'affine:surface') return;
    const boxedElements = entry.map.get('prop:elements');
    const elementsValue = isYMap(boxedElements)
      ? boxedElements.get('value')
      : undefined;
    if (!isYMap(elementsValue)) return;

    elementsValue.forEach((elementValue, elementId) => {
      if (!isYMap(elementValue)) return;
      surfaceElements.set(elementId, {
        id: elementId,
        map: elementValue,
        type: readString(elementValue, 'type'),
      });
    });
  });

  const elementBounds = new Map<string, LegacyArchiveXYWH>();
  const resolving = new Set<string>();

  const resolveAnyBound = (id: string): LegacyArchiveXYWH | null => {
    return blockBounds.get(id) ?? resolveElementBound(id);
  };

  const computeConnectorBound = (
    element: SurfaceElementEntry
  ): LegacyArchiveXYWH | null => {
    const sourcePoint = readConnectionPoint(
      element.map.get('source'),
      resolveAnyBound
    );
    const targetPoint = readConnectionPoint(
      element.map.get('target'),
      resolveAnyBound
    );
    let bound: MutableXYWH | null = null;

    for (const point of [sourcePoint, targetPoint]) {
      if (!point) continue;
      bound = unionBounds(bound, [point[0], point[1], 0, 0]);
    }

    const labelXYWH = element.map.get('labelXYWH');
    const parsedLabel = Array.isArray(labelXYWH)
      ? isXYWH(labelXYWH)
        ? labelXYWH
        : null
      : null;
    if (parsedLabel) {
      bound = unionBounds(bound, parsedLabel);
    }

    if (!bound) return null;

    const strokeWidth = readNumber(element.map, 'strokeWidth') ?? 1;
    return padBound(bound, Math.max(8, strokeWidth * 2));
  };

  const computeChildrenBound = (
    element: SurfaceElementEntry
  ): LegacyArchiveXYWH | null => {
    const childIds = collectChildIds(element.map.get('children'));
    let bound: MutableXYWH | null = null;

    for (const childId of childIds) {
      const childBound = resolveAnyBound(childId);
      if (!childBound) continue;
      bound = unionBounds(bound, childBound);
    }

    return bound;
  };

  function resolveElementBound(id: string): LegacyArchiveXYWH | null {
    const existing = elementBounds.get(id);
    if (existing) return existing;

    const element = surfaceElements.get(id);
    if (!element || resolving.has(id)) return null;

    resolving.add(id);
    try {
      const xywhValue = element.map.get('xywh');
      const parsed = parseLegacyArchiveXYWH(xywhValue);
      if (parsed && hasPositiveSize(parsed)) {
        elementBounds.set(id, parsed);
        return parsed;
      }

      const size = element.type
        ? SURFACE_ELEMENT_FALLBACK_SIZE_BY_TYPE.get(element.type)
        : undefined;
      const computed =
        element.type === 'connector'
          ? computeConnectorBound(element)
          : element.type === 'group' || element.type === 'mindmap'
            ? computeChildrenBound(element)
            : null;
      const fallback = computed ?? fallbackXYWH(xywhValue, size ?? [100, 100]);
      const normalized = normalizeXYWH(xywhValue, fallback);
      if (!normalized) {
        return null;
      }

      const repairedValue = serializeLegacyArchiveXYWH(normalized.xywh);
      element.map.set('xywh', repairedValue);
      changed = true;
      elementBounds.set(id, normalized.xywh);
      recordDiagnostic({
        action: 'repair',
        elementId: id,
        elementType: element.type,
        persistedValue: xywhValue,
        reason: normalized.reason,
        repairedValue,
        scope: 'surface-element',
      });
      return normalized.xywh;
    } finally {
      resolving.delete(id);
    }
  }

  for (const elementId of surfaceElements.keys()) {
    resolveElementBound(elementId);
  }

  return { changed, diagnostics };
}
